import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { House, Car, CreditCard, Wallet, PiggyBank, Receipt, Armchair, ArrowRight, Crown, Lock } from "@phosphor-icons/react";
import { Stat, money, useIsPro } from "@/components/finance/fin";

const CATS = [
  { k: "A", title: "Mortgage & Home", icon: House, tools: [["Mortgage calculator & payoff accelerator", "/finance/mortgage"], ["Prepayment allowance tracker", "/finance/mortgage"], ["Home affordability", null]] },
  { k: "B", title: "Vehicle Financing", icon: Car, tools: [["Vehicle payment & payoff accelerator", "/finance/vehicle"], ["Lease vs buy", null]] },
  { k: "C", title: "Debt Management", icon: CreditCard, tools: [["Credit card snowball vs avalanche", "/finance/debts"]] },
  { k: "D", title: "Budget & Expenses", icon: Wallet, tools: [["Monthly budget", "/finance/budget"], ["Net worth tracker", "/finance/net-worth"]] },
  { k: "E", title: "Savings & Investments", icon: PiggyBank, tools: [["Savings goals", "/finance/savings"], ["Compound interest", null], ["TFSA & RRSP", null]] },
  { k: "F", title: "Income & Taxes", icon: Receipt, tools: [["Tax Centre (T1 preparation)", "/tax"], ["Paycheque projections", "/projections"], ["Income tax estimator (all provinces)", null]] },
  { k: "G", title: "Retirement Planning", icon: Armchair, tools: [["Retirement & pension planner", "/retirement"], ["Retire-at-different-ages calculator", null]] },
];

function Summary() {
  const isPro = useIsPro();
  const { data: s } = useQuery({ queryKey: ["fin-summary"], queryFn: async () => (await api.get("/finance/summary")).data, enabled: isPro, retry: false });
  if (!isPro) return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 flex flex-wrap items-center gap-4" data-testid="fin-summary-locked">
      <Crown className="h-6 w-6 text-primary" weight="fill" />
      <p className="flex-1 text-sm">All calculators are free. Upgrade to <strong>Pro</strong> to save mortgages, loans and budgets, keep payment history and see your consolidated financial summary.</p>
      <Link to="/billing" className="text-sm font-semibold text-primary" data-testid="fin-upgrade-link">Upgrade</Link>
    </div>
  );
  if (!s) return null;
  if (!s.profiles && s.net_worth == null && s.monthly_income == null) return <p className="text-sm text-muted-foreground" data-testid="fin-summary-empty">Save a mortgage, loan, budget or net-worth snapshot to see your financial summary here.</p>;
  return (
    <section data-testid="fin-summary">
      <h2 className="text-base md:text-lg font-bold mb-3">Your financial summary</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Stat label="Net worth" value={money(s.net_worth, 0)} tone={s.net_worth >= 0 ? "good" : "bad"} testId="fin-sum-networth" />
        <Stat label="Total assets" value={money(s.total_assets, 0)} testId="fin-sum-assets" />
        <Stat label="Mortgage debt" value={money(s.mortgage_debt, 0)} testId="fin-sum-mortgage" />
        <Stat label="Vehicle debt" value={money(s.vehicle_debt, 0)} testId="fin-sum-vehicle" />
        <Stat label="Credit card debt" value={money(s.credit_card_debt, 0)} testId="fin-sum-cards" />
        <Stat label="Other loans" value={money(s.other_loans, 0)} testId="fin-sum-other" />
        <Stat label="Monthly income" value={money(s.monthly_income, 0)} testId="fin-sum-income" sub={s.budget_month} />
        <Stat label="Monthly expenses" value={money(s.monthly_expenses, 0)} testId="fin-sum-expenses" />
        <Stat label="Disposable / month" value={money(s.disposable, 0)} tone={s.disposable >= 0 ? "good" : "bad"} testId="fin-sum-disposable" />
        <Stat label="Extra principal paid" value={money(s.extra_principal, 0)} testId="fin-sum-extra" />
        <Stat label="Est. interest saved" value={money(s.interest_saved, 0)} tone="good" testId="fin-sum-saved" />
      </div>
      {s.errors?.length > 0 && <p className="text-xs text-amber-400 mt-2">{s.errors.join(" · ")}</p>}
    </section>
  );
}

export default function FinanceCentre() {
  return (
    <div className="animate-fade-up space-y-8" data-testid="finance-centre-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">My Financial Centre</p>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight mt-1">Plan every dollar</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl text-sm md:text-base">Mortgages, vehicles, debt, budgets and savings — real Canadian calculations alongside the pay you already track.</p>
      </div>
      <Summary />
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {CATS.map((c, idx) => (
          <div key={c.k} className="rounded-xl border border-border bg-card p-5 animate-fade-up" style={{ animationDelay: `${idx * 50}ms` }} data-testid={`fin-cat-${c.k}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><c.icon className="h-5 w-5" weight="duotone" /></div>
              <div><p className="text-[10px] font-bold text-muted-foreground">{c.k}</p><p className="font-bold">{c.title}</p></div>
            </div>
            <ul className="space-y-1">
              {c.tools.map(([t, to]) => (
                <li key={t}>{to ? (
                  <Link to={to} className="group flex items-center justify-between rounded-md px-2 py-2 text-sm hover:bg-secondary/60 transition-colors" data-testid={`fin-tool-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {t}<ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                ) : (
                  <span className="flex items-center justify-between px-2 py-2 text-sm text-muted-foreground"><span>{t}</span><span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider"><Lock className="h-3 w-3" /> Coming soon</span></span>
                )}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">All results are estimates for planning only and are not financial, legal or tax advice. Lenders calculate interest differently — confirm figures with your lender.</p>
    </div>
  );
}
